export interface IMobileUserData {
    access_token: string;
    client_id: string;
    id_token: string;
    privateKeyBase64: string;
    publicKeyBase64: string;
    refresh_token: string;
}

export interface IMobileTokens {
    idToken: string;
    refreshToken: string;
    sigKeyPair: CryptoKeyPair;
    token: string;
}

export interface IMobileProfile {
    clientId: string;
    tokens: IMobileTokens;
}

interface IMobileCallback {
    changeRole?: () => void;
    getLanguage?: () => string;
    getLoggedInUser: () => string;
    getRole?: () => string;
}

function getCallbackFun(): IMobileCallback | undefined {
    return (window as { Callback?: IMobileCallback }).Callback;
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
    return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0)).buffer as ArrayBuffer;
}

async function importKeyPair(publicKeyBase64: string, privateKeyBase64: string): Promise<CryptoKeyPair> {
    const publicKey = await crypto.subtle.importKey(
        'spki',
        base64ToArrayBuffer(publicKeyBase64),
        { name: 'ECDSA', namedCurve: 'P-256' },
        true,
        ['verify'],
    );

    const privateKey = await crypto.subtle.importKey(
        'pkcs8',
        base64ToArrayBuffer(privateKeyBase64),
        { name: 'ECDSA', namedCurve: 'P-256' },
        true,
        ['sign'],
    );

    return { privateKey, publicKey };
}

export const mobileBridge = {
    getLanguage(): string {
        try {
            return getCallbackFun()?.getLanguage?.() ?? 'en';
        } catch {
            return 'en';
        }
    },

    getRole(): string {
        try {
            return getCallbackFun()?.getRole?.() ?? 'Institutional Building:SHG Bookkeeper';
        } catch {
            return '';
        }
    },

    async getUserProfile(): Promise<Partial<IMobileProfile>> {
        try {
            const callbackFun = getCallbackFun();
            if (!callbackFun) {
                return {};
            }

            const userData = JSON.parse(callbackFun.getLoggedInUser()) as IMobileUserData;
            const { access_token, client_id, id_token, privateKeyBase64, publicKeyBase64, refresh_token } = userData;
            const { privateKey, publicKey } = await importKeyPair(publicKeyBase64, privateKeyBase64);

            return {
                tokens: {
                    idToken: id_token,
                    refreshToken: refresh_token,
                    sigKeyPair: { privateKey, publicKey },
                    token: access_token,
                },
                clientId: client_id,
            };
        } catch (error) {
            // Without this log, malformed getLoggedInUser() JSON, an unavailable
            // crypto.subtle and a failed ECDSA key import are all indistinguishable
            // from "the native bridge is not there".
            console.error('Mobile bridge: unable to read the logged-in user profile', error);
            return {};
        }
    },

    changeRole(): void {
        try {
            getCallbackFun()?.changeRole?.();
        } catch {
            // ignore
        }
    },

    isPWA(): boolean {
        try {
            // return !!getCallbackFun()?.getLoggedInUser;
            return true;
        } catch {
            return false;
        }
    },

    /**
     * Waits for the native host to inject `window.Callback`, which can land after
     * Angular has bootstrapped. Callers that run at bootstrap (before the router has
     * evaluated any guard) must await this, otherwise they see no bridge and fall back
     * to the web authentication flow.
     * @param timeoutMs how long to wait before giving up
     * @param intervalMs how often to re-check
     * @returns whether the bridge became available
     */
    async waitForBridge(timeoutMs = 3000, intervalMs = 50): Promise<boolean> {
        const deadline = Date.now() + timeoutMs;

        while (!mobileBridge.isPWA()) {
            if (Date.now() >= deadline) {
                return false;
            }
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }

        return true;
    },
};
